import { useEffect } from 'react';
import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { apiClient } from '../api/client';
import type {
  NotificationsPage,
  NotificationPreferences,
} from '../api/notificationsTypes';
import { useAuthStore } from '../stores/authStore';
import { useUIStore } from '../stores/uiStore';

// WS-4a (C1, C1.1). React Query bindings for the bell + slide-over.
//
// Unread-count polling: spec/12 says SSE/polling lives in C3. This
// slice uses a 60-second polling interval per the WS-4a brief — the
// SSE migration is a later workstream. The polling query also writes
// the count into `uiStore.notifications.unreadCount` so the header
// bell can read it without a query subscription.

export const notificationsKeys = {
  list: (filters: { page: number; filter: 'all' | 'unread'; type?: string }) =>
    ['notifications', filters] as const,
  unread: ['notifications', 'unread-count'] as const,
  preferences: ['notifications', 'preferences'] as const,
};

const POLL_INTERVAL_MS = 60_000;

export interface NotificationsListFilters {
  page: number;
  limit?: number;
  filter: 'all' | 'unread';
  type?: string;
}

export function useNotificationsList(filters: NotificationsListFilters) {
  return useQuery({
    queryKey: notificationsKeys.list({
      page: filters.page,
      filter: filters.filter,
      type: filters.type,
    }),
    queryFn: async (): Promise<NotificationsPage> => {
      const params = new URLSearchParams();
      params.set('page', String(filters.page));
      if (filters.limit) params.set('limit', String(filters.limit));
      params.set('filter', filters.filter);
      if (filters.type) params.set('type', filters.type);
      const res = await apiClient.get<NotificationsPage>(
        `/notifications?${params.toString()}`,
      );
      return res.data;
    },
    staleTime: 30 * 1000,
  });
}

// 60s poll of the unread count. Synchronises into `uiStore` so the
// bell badge can render outside the query subscription. Only runs
// while the user is authenticated.
//
// WS-4b correction (C3): the browser cannot authenticate against the
// `/api/v1/sse/events` route under the current auth model — the
// EventSource API cannot send an Authorization header, and the
// `/api/v1/auth`-scoped refresh cookie does not ride along to
// `/api/v1/sse/...`. The SSE route is real and works for clients
// that can set a bearer header (server-to-server, internal tooling),
// but the browser client stays on the existing 60s poll. See the C3
// row in the gap matrix (the SSE/browser piece is split as C3.1
// FAIL) for the closure path (short-lived URL token or a new
// cookie-auth SSE handshake).
export function useUnreadCountPoll(): void {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const setUnreadCount = useUIStore((s) => s.setUnreadCount);
  const { data } = useQuery({
    queryKey: notificationsKeys.unread,
    queryFn: async (): Promise<{ unreadCount: number }> => {
      const res = await apiClient.get<{ unreadCount: number }>(
        '/notifications/unread-count',
      );
      return res.data;
    },
    enabled: isAuthenticated,
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    staleTime: POLL_INTERVAL_MS / 2,
  });
  useEffect(() => {
    if (typeof data?.unreadCount === 'number') {
      setUnreadCount(data.unreadCount);
    }
  }, [data?.unreadCount, setUnreadCount]);
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.patch(`/notifications/${id}/read`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await apiClient.post<{
        success: true;
        markedCount: number;
      }>('/notifications/read-all');
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useMyNotificationPreferences() {
  return useQuery({
    queryKey: notificationsKeys.preferences,
    queryFn: async (): Promise<NotificationPreferences> => {
      const res = await apiClient.get<{ preferences: NotificationPreferences }>(
        '/notifications/preferences',
      );
      return res.data.preferences;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateMyNotificationPreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: NotificationPreferences) => {
      const res = await apiClient.put<{ preferences: NotificationPreferences }>(
        '/notifications/preferences',
        input,
      );
      return res.data.preferences;
    },
    onSuccess: (data) => {
      qc.setQueryData(notificationsKeys.preferences, data);
    },
  });
}
